import { Box, Button, Grid2, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import type { FC } from 'react';
import type { DropzoneOptions, FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import { Progress } from '@components/Progress';

import { MAX_FILES_DROP, MAX_FILE_SIZE_MB } from '@constants/files';

import { useCuration } from '@hooks/useCuration';

import { useSnackbar } from '../../hooks/useSnackbar';
import { FileItem } from './FileItem';

const messages = defineMessages({
  chooseFiles: {
    id: 'fileDropzone.chooseFiles',
    defaultMessage: 'Drag and drop files here, or browse files',
  },
  removeAll: {
    id: 'fileDropzone.removeAll',
    defaultMessage: 'Remove All',
  },
  remove: {
    id: 'fileDropzone.remove',
    defaultMessage: 'Remove',
  },
  accept: {
    id: 'fileDropzone.fileTypes',
    defaultMessage: 'Valid file types are: {accept}',
  },
  upload: {
    id: 'GLOBAL.upload',
    defaultMessage: 'Upload',
  },
  fileLimitError: {
    id: 'fileDropzone.fileLimitError',
    defaultMessage: 'Cannot add more than {limit} files',
  },
  addFiles: {
    id: 'fileDropzone.addFiles',
    defaultMessage: 'Add File(s)',
  },
});

interface FileDropzoneProps extends Omit<
  DropzoneOptions,
  'onDrop' | 'onError'
> {
  /**
   * Callback that fires when the upload button is clicked
   */
  onUpload?: (files: DroppedFile[]) => Promise<void>;
  progress?: number;
  label?: string;
  onFileChange?: (files: DroppedFile[]) => void;
  /**
   * Initial files to display in the dropzone
   */
  initialFiles?: DroppedFile[];
}

/**
 * File dropzone component
 */
const FileDropzone: FC<FileDropzoneProps> = ({
  accept = {
    'image/jpeg': ['.jpeg', '.jpg'],
    'image/png': ['.png'],
    'application/pdf': ['.pdf'],
    'application/xml': ['.xml'],
    'text/xml': ['.xml'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
      '.docx',
    ],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
      '.xlsx',
    ],
    ['application/zip']: ['.zip'],
  },
  maxFiles = MAX_FILES_DROP,
  maxSize = MAX_FILE_SIZE_MB * 1000 * 1000,
  minSize,
  onUpload,
  progress,
  label,
  onFileChange,
  initialFiles,
  ...other
}) => {
  const [files, setFiles] = React.useState<DroppedFile[]>(initialFiles || []);
  const [loading, setLoading] = React.useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();
  const {
    state: { isCreating },
  } = useCuration();

  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      setFiles(initialFiles);
    }
  }, [initialFiles]);

  useEffect(() => {
    onFileChange?.(files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const handleDrop = <T extends File>(
    acceptedFiles: T[],
    rejectedFiles: FileRejection[]
  ) => {
    if (loading) {
      return;
    }
    const acceptedMaxFiles = acceptedFiles.slice(
      0,
      MAX_FILES_DROP - files.length
    );
    if (acceptedMaxFiles.length > 0) {
      const newFiles = [...files];
      // Make filenames unique
      acceptedMaxFiles.forEach((file) => {
        const name = file.name.slice(0, file.name.lastIndexOf('.'));
        const extension = file.name.slice(file.name.lastIndexOf('.'));
        const existing = newFiles.filter(
          (f) =>
            `${f.baseName}${extension}` === file.name || f.name === file.name
        ).length;
        const newName = `${name.trim()}${
          existing > 0 ? ` (${existing})` : ''
        }${extension}`;
        const newFile = new File([file], newName, {
          type: file.type,
          lastModified: file.lastModified,
        }) as DroppedFile;
        newFile.baseName = name;
        newFiles.push(newFile);
      });
      setFiles(newFiles);
    }
    if (
      acceptedMaxFiles.length !== acceptedFiles.length ||
      rejectedFiles.length >= MAX_FILES_DROP
    ) {
      // # files exceeds limit
      const maxFileErr = formatMessage(messages.fileLimitError, {
        limit: maxFiles,
      });
      enqueueSnackbar({
        message: maxFileErr,
        variant: 'error',
      });
    } else if (
      rejectedFiles.length > 0 ||
      acceptedMaxFiles.length !== acceptedFiles.length
    ) {
      // File-level error
      const messages = rejectedFiles.map(({ errors, file }) =>
        errors.map(({ message }) => `${file.name}: ${message}`).join('\n')
      );
      enqueueSnackbar({
        message: messages.join('\n'),
        variant: 'error',
      });
    }
  };

  const handleRemove = (file: File) => {
    if (loading) {
      return;
    }
    setFiles((prev) => {
      const newFiles = prev.filter((f) => f.name !== file.name);
      return newFiles;
    });
  };

  const handleRemoveAll = () => {
    if (loading) {
      return;
    }
    setFiles([]);
  };

  const handleUpload = async () => {
    if (loading) {
      return;
    }
    setLoading(true);
    try {
      await onUpload?.(files);
      // eslint-disable-next-line no-empty
    } catch {
    } finally {
      setFiles([]);
      setLoading(false);
    }
  };

  /*
   * We did not add the remaining props to avoid component complexity
   * but you can simply add it if you need to.
   */
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    maxFiles,
    maxSize,
    minSize,
    onDrop: handleDrop,
    disabled: loading,
  });

  const rootProps = getRootProps();
  const inputProps = getInputProps();

  return (
    <>
      <div {...other}>
        <Box
          p={1}
          sx={{
            alignItems: 'center',
            border: 1,
            borderRadius: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
            outline: 'none',
            ...(isDragActive && {
              backgroundColor: 'action.active',
              opacity: 0.5,
            }),
            ...(loading && {
              backgroundColor: '#CCCCCC',
              opacity: 0.5,
            }),
            ...(!loading && {
              '&:hover': {
                backgroundColor: 'action.hover',
                cursor: 'pointer',
                opacity: 0.5,
              },
            }),
          }}
          {...rootProps}
        >
          <input {...inputProps} />
          <Grid2 container spacing={1}>
            <Grid2>
              <Icon name="paperclip" size="sm" type="far" />
            </Grid2>
            <Grid2>{label ?? formatMessage(messages.chooseFiles)}</Grid2>
          </Grid2>
        </Box>
        <Typography variant="caption">
          {formatMessage(messages.accept, {
            accept: Object.values(accept).flat().join(', '),
          })}
        </Typography>
        {files.length ? (
          <Box sx={{ mt: 2 }}>
            <Grid2 container spacing={3}>
              {files.map((file, index) => (
                <FileItem
                  file={file}
                  key={`file-item-${index}`}
                  onRemove={handleRemove}
                />
              ))}
            </Grid2>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mt: 2,
              }}
            >
              <Button
                color="primary"
                disabled={loading}
                onClick={handleRemoveAll}
                size="small"
                type="button"
                variant="text"
              >
                {formatMessage(messages.removeAll)}
              </Button>
              {!isCreating && (
                <Button
                  color="primary"
                  disabled={loading}
                  onClick={handleUpload}
                  size="small"
                  sx={{ ml: 2 }}
                  type="button"
                  variant="contained"
                >
                  {formatMessage(messages.upload)}
                </Button>
              )}
            </Box>
          </Box>
        ) : null}
      </div>
      {typeof progress === 'number' && loading ? (
        <Progress value={progress} />
      ) : null}
    </>
  );
};

export default FileDropzone;
